import { Box, type BoxProps, IconButton, Input, type InputProps } from '@chakra-ui/react'
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons'
import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'

import logger from '@/utils/logger'

import Icon from '@/components/UI/Icon/Icon'

export function useSearch(queryKey = 'search'): string {
  const router = useRouter()
  const searchStr = router.query[queryKey]
  if (typeof searchStr === 'string') {
    return decodeURIComponent(`${searchStr}`)
  }
  return ''
}

export function SearchBar({
  queryKey = 'search',
  placeholder = '',
  inputProps,
  iconOffset = '0px',
  ...props
}: {
  queryKey?: string
  placeholder?: string
  inputProps?: InputProps
  iconOffset?: string
} & BoxProps): JSX.Element {
  const router = useRouter()
  const searchParam = useSearch(queryKey)

  const [currentInput, setCurrentInput] = useState(searchParam)

  const handleSearch = useCallback(
    (searchInput: string) => {
      router
        .push({
          query: {
            ...router.query,
            [queryKey]: encodeURIComponent(searchInput),
          },
        })
        .catch(err => {
          logger.error('Navigation failed', { err })
        })
    },
    [queryKey, router]
  )

  const iconPositioning = { right: iconOffset, top: '50%', transform: 'translateY(-50%)' }

  return (
    <Box position="relative" {...props}>
      <Input
        value={currentInput}
        onChange={e => setCurrentInput(e.target.value)}
        placeholder={placeholder}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            handleSearch(currentInput)
          }
        }}
        variant="normal"
        pr="32px"
        {...inputProps}
      />
      <Box position="absolute" {...iconPositioning}>
        <IconButton
          variant="ghost"
          icon={<Icon faIcon={faMagnifyingGlass} size="md" />}
          onClick={() => handleSearch(currentInput)}
          aria-label="Search"
        />
      </Box>
    </Box>
  )
}
